import { _ } from "../../../core/polyglot/polyglot.js";
import { confirmDialog } from "../../../core/widgets/dialogs/dialogs.js";
import EmailContent from "../../../models/EmailContent.js";
import EmailNotificationWidget from "./EmailNotificationWidget/EmailNotificationWidget.js";

/**
 * Renderer used to have a button showing a notification dialog. This
 * dialog lets the user choose the recipients, title and content of
 * the report email notification, then it sends the emails batch.
 *
 * @param {renderable} [label] - Name of the button
 * @param {User} currentUser - Sender of the email. It can be used as
 *   recipient as well
 * @param {CompanyUser[]} companyUsers - Potential recipients for the
 *   email related to the company the report belongs to
 * @param {AgencyUser[]} agencyUsers - Potential recipients for the
 *   email related to the agency the report's company belongs to
 * @param {Report} report - The report that will be sent to the
 *   recipients
 * @param {Company} company - The company the report belongs to
 * @param {string} cssClass - Extra css classes for the button
 * @param {function} sendNotifications - Callback to send the
 *   emails. Takes an EmailContent as argument
 */
export default function makeReportNotificationButton({
	label = _("Send mail notification"),
	currentUser,
	companyUsers = [],
	agencyUsers = [],
	report,
	company,
	cssClass = "",
	sendNotifications,
}) {
	const emailContent = makeEmailContent({ currentUser, report });

	return (html) =>
		html.a(
			{
				klass: ["btn", cssClass],
				"data-test": "send-mail-notification",
				click: () =>
					showNotificationDialog({
						emailContent,
						companyUsers,
						agencyUsers,
						company,
						sendNotifications,
					}),
			},
			label,
		);
}

function showNotificationDialog({
	emailContent,
	companyUsers,
	agencyUsers,
	company,
	sendNotifications,
}) {
	const emailEditor = makeEmailEditor({
		emailContent,
		companyUsers,
		agencyUsers,
		company,
		sendNotifications,
	});

	confirmDialog({
		title: _("Notify users"),
		body: emailEditor,
		contentCssClass: "resizable-modal-content",
		callback: () => emailEditor.submit(),
	}).show();
}

function makeEmailEditor({
	emailContent,
	companyUsers,
	agencyUsers,
	company,
	sendNotifications,
}) {
	return new EmailNotificationWidget({
		companyUsers,
		agencyUsers,
		currentCompany: company,
		emailContent,
		submitCallback: () => sendNotifications(emailContent),
	});
}

function makeEmailContent({ report, currentUser }) {
	const emailContent = new EmailContent({ sender: currentUser });

	return report ? emailContent.visit(report) : emailContent;
}
